import { useRouteLocale } from "@/hooks/useRouteLocale";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import apiClient from "@/services/api/client";
import { Button } from "@/components/ui/button";
import { publishEvent, type Event } from "@/services/api/events";
import { SpanStatusCode } from "@opentelemetry/api";
import { useTraceSection, withTrace } from "@/hooks/useTraceSection";
import { WhatsAppShareSection } from "./WhatsAppShareSection";
import { UrlCopySection } from "./UrlCopySection";
import { QrCodeDownloadSection } from "./QrCodeDownloadSection";

interface ShareEventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  event: Event | null;
  onPublished?: (event: Event) => void;
}

export function ShareEventModal({
  open,
  onOpenChange,
  event,
  onPublished,
}: ShareEventModalProps) {
  const { t } = useTranslation();
  const locale = useRouteLocale();
  const currentLocale = locale || "ro";

  const [shareUrl, setShareUrl] = useState<string>("");
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [invitationText, setInvitationText] = useState<string>("");
  const [isLive, setIsLive] = useState<boolean>(Boolean(event?.live));
  const [publishing, setPublishing] = useState(false);

  // Keep local live flag in sync with the event passed in
  useEffect(() => {
    setIsLive(Boolean(event?.live));
  }, [event?.uuid, event?.live]);

  const handlePublishNow = async () => {
    if (!event?.uuid || publishing) return;

    try {
      setPublishing(true);
      const updated = await publishEvent(event.uuid);
      setIsLive(Boolean(updated.live));
      toast.success(t("events.publishSuccess"));
      onPublished?.(updated);
    } catch (error) {
      console.error("Failed to publish event from share modal:", error);
      toast.error(t("events.publishError"));
    } finally {
      setPublishing(false);
    }
  };

  const fetchShareInfo = useCallback(async () => {
    if (!event?.uuid) return;

    await withTrace("shareModal", "fetchShareData", async (span) => {
      span.setAttribute("event.uuid", event.uuid);
      span.setAttribute("share.locale", currentLocale);
      span.setAttribute("event.name", event.name ?? "");
      span.addEvent("share.modal.opened");

      try {
        setLoading(true);
        type SharePayload = { url: string; qrCodeDataUrl?: string };
        type ShareResponse = SharePayload | { data: SharePayload };

        const response = await apiClient.get<ShareResponse>(`/events/${event.uuid}/share`, {
          params: { locale: currentLocale },
        });

        span.addEvent("share.api.response", {
          "http.status_code": response.status,
        });

        const raw = response.data;
        const data: SharePayload =
          "data" in raw && raw.data ? raw.data : (raw as SharePayload);

        if (!data || !data.url) {
          span.setStatus({
            code: SpanStatusCode.ERROR,
            message: "share-data-missing-url",
          });
          console.error("Invalid share data received:", data);
          toast.error(t("events.share.error"));
          return;
        }

        setShareUrl(data.url);
        setQrCodeDataUrl(data.qrCodeDataUrl || "");

        const defaultTextTemplate = t("events.share.defaultInvitation");
        const defaultText = defaultTextTemplate
          .replace("{{eventName}}", event?.name || "Event")
          .replace("{{eventUrl}}", data.url);
        setInvitationText(defaultText);

        span.setAttribute(
          "share.invitation.default_length",
          defaultText.length,
        );
        span.setAttribute("share.qr.available", data.qrCodeDataUrl ? 1 : 0);
      } catch (error) {
        const err = error as Error;
        span.recordException(err);
        console.error("Failed to fetch share info:", error);
        toast.error(t("events.share.error"));
      } finally {
        setLoading(false);
      }
    }, {
      eventUuid: event.uuid,
      locale: currentLocale,
    });
  }, [event?.uuid, event?.name, currentLocale, t]);

  useTraceSection(
    "shareModal",
    open ? "open" : "closed",
    {
      eventUuid: event?.uuid ?? "",
      locale: currentLocale,
    },
    open,
  );

  useEffect(() => {
    if (open && event?.uuid) {
      fetchShareInfo();
    }
  }, [open, event?.uuid, fetchShareInfo]);




  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        onOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }
  }, [open, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogClose onClose={() => onOpenChange(false)} />
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold">
            {t("events.share.title")}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground mt-1">
            {t("events.share.description")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {!isLive && (
            <div
              data-testid="share-not-live-warning"
              className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  {t("events.share.notLiveWarning")}
                </p>
              </div>
              <Button
                data-testid="share-publish-now"
                size="sm"
                onClick={handlePublishNow}
                disabled={publishing}
                className="bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white"
              >
                {publishing ? t("common.loading") : t("events.share.publishNow")}
              </Button>
            </div>
          )}
          <div className="space-y-4">
            <WhatsAppShareSection
              invitationText={invitationText}
              onInvitationTextChange={setInvitationText}
              shareUrl={shareUrl}
              loading={loading}
              eventUuid={event?.uuid}
            />
            <UrlCopySection
              shareUrl={shareUrl}
              loading={loading}
              eventUuid={event?.uuid}
            />
            <QrCodeDownloadSection
              qrCodeDataUrl={qrCodeDataUrl || null}
              loading={loading}
              eventUuid={event?.uuid}
              eventName={event?.name}
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
